import {
  Badge,
  Button,
  Drawer,
  Form,
  Grid,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/lib/table/interface';
import { FilterOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FourZeroThree from '../../FourZeroThree';
import FiveHundred from '../../FiveHundred';
import TableToolbar from '../../table/TableToolbar';
import UserRankDropdown from '../../users/common/UserRankDropdown';
import { CreditLimitAnalysisData } from '../../../types';
import CreditLimitDetailModal from '../../users/CreditLimitDetailModal';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import { alertMessage } from '../../../utils/alertMessage';
import { setFont } from '../../../utils/colComponents';
import { useTab } from '../../../hooks/useTab';
import { dashboardRoute } from '../../../constants/pathname';
import { hasPermission } from '../../../utils/hasPermission';
import { actionPermissions } from '../../../constants/actionPermissions';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import {
  EXTENDED_TIMEOUT,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import { mergeParamsToString } from '../../../utils/helperFunction';
import { RED_LIGHT } from '../../../constants/color';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import SellersDropdown from '../../sellers/SellersDropdown';

type SortField = 'TOTAL_UNPAID_AMOUNT' | 'SHIPPED_AMOUNT' | 'UNSHIPPED_AMOUNT';

const UserCreditLimitAnalysis = () => {
  const { t } = useTranslation();
  const { addTab } = useTab();
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const formRef = useRef<any>(null);
  const [total, setTotal] = useState<number>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const [fourZeroThree, setFourZeroThree] = useState<boolean>(false);
  const [fiveHundred, setFiveHundred] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<CreditLimitAnalysisData[]>([]);
  const [sortValue, setSortValue] = useState<SortField>('TOTAL_UNPAID_AMOUNT');
  const [sortOrder, setSortOrder] = useState<string>('DESC');
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const [showDrawer, setShowDrawer] = useState(false);
  const [advance, setAdvance] = useState<{ [key: string]: any }>({});
  const [isSeller, setIsSeller] = useState<boolean>(false);
  // 详情 Modal
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailUserId, setDetailUserId] = useState<number>();
  const [detailUserName, setDetailUserName] = useState<string>();

  const openDetail = (record: CreditLimitAnalysisData) => {
    setDetailUserId(record.user?.userId);
    setDetailUserName(record.user?.userName);
    setDetailVisible(true);
  };

  const userLink = (record: CreditLimitAnalysisData, label?: string | number) =>
    record.user ? (
      <Button
        style={{ padding: 0 }}
        type="link"
        onClick={() =>
          addTab(
            '',
            `${dashboardRoute.users.detail}?user_id=${record.user.userId}`
          )
        }
        disabled={!hasPermission(actionPermissions.userGroup.userView)}
      >
        {label}
      </Button>
    ) : null;

  const columns: ColumnsType<CreditLimitAnalysisData> = [
    {
      title: setFont(t('analysis.userCreditLimit.userId')),
      key: 'userId',
      dataIndex: 'userId',
      width: 90,
      render: (_: any, record: CreditLimitAnalysisData) =>
        userLink(record, record.user?.userId),
    },
    {
      title: setFont(t('analysis.userCreditLimit.nickname')),
      key: 'nickName',
      dataIndex: 'nickName',
      width: 120,
      render: (_: any, record: CreditLimitAnalysisData) => record.user?.nickName,
    },
    {
      title: setFont(t('analysis.userCreditLimit.mobilePhone')),
      key: 'mobilePhone',
      dataIndex: 'mobilePhone',
      width: 120,
      render: (_: any, record: CreditLimitAnalysisData) =>
        record.user?.mobilePhone,
    },
    {
      title: setFont(t('analysis.userCreditLimit.creditLimit')),
      key: 'creditLimit',
      dataIndex: 'creditLimit',
      width: 110,
      render: (value: number, record: CreditLimitAnalysisData) =>
        record.unlimited ? t('analysis.userCreditLimit.unlimited') : value,
    },
    {
      title: setFont(t('analysis.userCreditLimit.unshippedAmount')),
      key: 'unshippedAmount',
      dataIndex: 'unshippedAmount',
      width: 130,
      sorter: true,
    },
    {
      title: setFont(t('analysis.userCreditLimit.shippedAmount')),
      key: 'shippedAmount',
      dataIndex: 'shippedAmount',
      width: 130,
      sorter: true,
      render: (value: number, record: CreditLimitAnalysisData) => (
        <span style={record.isOverCreditLimit ? { fontWeight: 600 } : undefined}>
          {value}
        </span>
      ),
    },
    {
      title: setFont(t('analysis.userCreditLimit.totalUnpaidAmount')),
      key: 'totalUnpaidAmount',
      dataIndex: 'totalUnpaidAmount',
      width: 130,
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: setFont(t('analysis.userCreditLimit.overLimit')),
      key: 'overLimit',
      dataIndex: 'overLimit',
      width: 90,
      render: (_: any, record: CreditLimitAnalysisData) =>
        record.isOverCreditLimit ? (
          <Tag color="red">{t('analysis.userCreditLimit.overLimitYes')}</Tag>
        ) : (
          <Tag color="green">{t('analysis.userCreditLimit.overLimitNo')}</Tag>
        ),
    },
    {
      title: setFont(t('actionsColumn.title')),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: any, record: CreditLimitAnalysisData) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => openDetail(record)}
        >
          {t('actionsColumn.view')}
        </Button>
      ),
    },
  ];

  const columnKeys = [
    'userId',
    'nickName',
    'mobilePhone',
    'creditLimit',
    'unshippedAmount',
    'shippedAmount',
    'totalUnpaidAmount',
    'overLimit',
  ];

  const getFormParams = useCallback(() => {
    return {
      page: page,
      size: pageSize,
      sortValue: sortValue,
      sortOrder: sortOrder,
      isSeller: isSeller,
      sellerId:
        isSeller && formRef.current && form.getFieldValue('sellerId')
          ? form.getFieldValue('sellerId')
          : undefined,
      memberId:
        formRef.current && form.getFieldValue('memberId')?.length
          ? form.getFieldValue('memberId').join()
          : undefined,
      rankId:
        formRef.current && form.getFieldValue('rankId')?.length
          ? form.getFieldValue('rankId').join()
          : undefined,
    };
  }, [page, pageSize, sortValue, sortOrder, isSeller, form]);

  const getData = useCallback(() => {
    setIsLoading(true);
    getDataWithAuthToken('analysis/user/credit_limit', {
      params: getFormParams(),
    })
      .then((response) => {
        if (response && response.goodStatus) {
          setData(response.data.list);
          setTotal(response.data.totalItem);
        } else if (response && response.returnCode === 403) {
          setFourZeroThree(true);
        } else {
          setFiveHundred(true);
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  }, [t, getFormParams]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!isSeller) form.resetFields(['sellerId']);
  }, [isSeller, form]);

  // 只处理排序（翻页交给 pagination.onChange）。必须用 extra.action 判断，
  // 否则默认排序(totalUnpaidAmount desc)一直存在，翻页时会带着 sorter 进来再次 setPage(1) 把页码打回第 1 页
  const onTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<CreditLimitAnalysisData>
      | SorterResult<CreditLimitAnalysisData>[],
    extra: TableCurrentDataSource<CreditLimitAnalysisData>
  ) => {
    if (extra.action !== 'sort' || Array.isArray(sorter)) return;
    const fieldMap: { [key: string]: SortField } = {
      unshippedAmount: 'UNSHIPPED_AMOUNT',
      shippedAmount: 'SHIPPED_AMOUNT',
      totalUnpaidAmount: 'TOTAL_UNPAID_AMOUNT',
    };
    const field = sorter.order
      ? fieldMap[sorter.columnKey as string]
      : undefined;
    if (field) {
      setPage(1);
      setSortValue(field);
      setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
    }
  };

  const advancedSearch = (
    <Drawer
      visible={showDrawer}
      closable={true}
      onClose={() => setShowDrawer(false)}
      placement="right"
      headerStyle={{ padding: '10px 3px 10px 3px' }}
      width={screens.sm ? 600 : 350}
      bodyStyle={{ padding: 10 }}
      title={t('order.orderListColumns.advancedSearch.title')}
    >
      <Form layout="vertical" form={form} ref={formRef}>
        {isSeller && (
          <Form.Item
            name="sellerId"
            label={t('order.orderListColumns.advancedSearch.sellerId')}
            style={{ marginBottom: 12 }}
          >
            <SellersDropdown
              onChange={(value: string) => {
                setAdvance((prev) => ({ ...prev, sellerId: value }));
              }}
            />
          </Form.Item>
        )}
        <Form.Item
          name="memberId"
          label={t('users.userListColumns.userMember')}
          style={{ marginBottom: 12 }}
        >
          <UserRankDropdown
            isMember
            isMultiple={true}
            onChange={(value: number[]) => {
              setAdvance((prev) => ({
                ...prev,
                memberId: value && value.length ? value : undefined,
              }));
            }}
          />
        </Form.Item>
        <Form.Item
          name="rankId"
          label={t('users.userListColumns.rankName')}
          style={{ marginBottom: 12 }}
        >
          <UserRankDropdown
            isMultiple={true}
            onChange={(value: number[]) => {
              setAdvance((prev) => ({
                ...prev,
                rankId: value && value.length ? value : undefined,
              }));
            }}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (page !== 1) setPage(1);
                else {
                  if (typingTimeout) clearTimeout(typingTimeout);
                  setTypingTimeout(setTimeout(() => getData(), GENERAL_TIMEOUT));
                }
              }}
            >
              {t('order.orderListColumns.advancedSearch.search')}
            </Button>
            <Button
              disabled={Object.values(advance).every((value) => !value)}
              onClick={() => {
                form.resetFields();
                setAdvance({});
                if (page !== 1) setPage(1);
                else {
                  if (typingTimeout) clearTimeout(typingTimeout);
                  setTypingTimeout(setTimeout(() => getData(), GENERAL_TIMEOUT));
                }
              }}
            >
              {t('order.orderListColumns.advancedSearch.reset')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Drawer>
  );

  return fourZeroThree ? (
    <FourZeroThree />
  ) : fiveHundred ? (
    <FiveHundred />
  ) : (
    <Spin spinning={isLoading} style={{ width: '100%' }}>
      <Space>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          {t('analysis.userCreditLimit.tab6Title')}
        </Typography.Title>
        {getDashboardStyle().isSellerSwitch && (
          <Switch
            loading={isLoading}
            onChange={(checked) => {
              setAdvance((prev) => ({ ...prev, sellerId: undefined }));
              if (typingTimeout) clearTimeout(typingTimeout);
              setTypingTimeout(
                setTimeout(() => {
                  setIsSeller(checked);
                  setPage(1);
                }, EXTENDED_TIMEOUT)
              );
            }}
            checkedChildren={t('goods.goodsListColumns.seller')}
            unCheckedChildren={t('goods.goodsListColumns.self')}
          />
        )}
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t('analysis.userCreditLimit.subtitle')}
      </Typography.Paragraph>
      <TableToolbar
        refresh={() => getData()}
        totalItems={total}
        columns={columns}
        columnKeys={columnKeys}
        rows={data.map((row) => ({
          userId: row.user ? row.user.userId : undefined,
          userName: row.user ? row.user.userName : undefined,
          nickName: row.user ? row.user.nickName : undefined,
          mobilePhone: row.user ? row.user.mobilePhone : undefined,
          creditLimit: row.unlimited
            ? t('analysis.userCreditLimit.unlimited')
            : row.creditLimit,
          unshippedAmount: row.unshippedAmount,
          shippedAmount: row.shippedAmount,
          totalUnpaidAmount: row.totalUnpaidAmount,
          overLimit: row.isOverCreditLimit
            ? t('analysis.userCreditLimit.overLimitYes')
            : t('analysis.userCreditLimit.overLimitNo'),
        }))}
        exportConfig={{
          fileName: 'USER_CREDIT_LIMIT',
          url: mergeParamsToString(
            'analysis/user/export/credit_limit?',
            getFormParams()
          ),
        }}
        advancedSearch={
          <Badge
            count={Object.keys(advance).reduce(
              (acc, key) => (advance[key] ? acc + 1 : acc),
              0
            )}
          >
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowDrawer((prev) => !prev)}
            >
              {t('order.orderListColumns.advancedSearch.title')}
            </Button>
            {advancedSearch}
          </Badge>
        }
      />
      <Table<CreditLimitAnalysisData>
        onChange={onTableChange}
        dataSource={data}
        rowKey={(obj) => obj.user.userId}
        columns={columns}
        scroll={{ y: 500, x: 800 }}
        loading={isLoading}
        onRow={(record) => ({
          style: record.isOverCreditLimit
            ? { backgroundColor: RED_LIGHT }
            : undefined,
        })}
        pagination={{
          total: total,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) =>
            t('general.paginationTotal', {
              start: range[0],
              end: range[1],
              total: total,
            }),
          size: 'small',
          defaultPageSize: Number(pageSize),
          onChange: (page, pSize) => {
            setPage(page);
            setPageSize((pSize || pageSize).toString());
          },
          current: page,
        }}
        size="small"
      />
      <CreditLimitDetailModal
        userId={detailUserId}
        userName={detailUserName}
        isSeller={isSeller}
        sellerId={
          isSeller && formRef.current && form.getFieldValue('sellerId')
            ? form.getFieldValue('sellerId')
            : undefined
        }
        visible={detailVisible}
        setVisible={setDetailVisible}
      />
    </Spin>
  );
};

export default UserCreditLimitAnalysis;
